/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { AdcsApiService, SecurityApiService } from '../../../../shared/http';
import { AdcsUtilsService, NamespaceService } from '../../../../shared/services/utils';
import { BaseComponent } from '../../../../../../core/components';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { DrawerService, LogService, NavigationService, RbacService, UtilsService } from '../../../../../../core/services';
import { ErrorResponseModel } from '../../../../../../core/models';
import { Observable } from 'rxjs';
import { SecurityDetailsModel } from '../../../../shared/models';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-insight-details',
    templateUrl: './insight-details.component.html',
    styleUrls: ['./insight-details.component.scss', '../insights.component.scss']
})
export class InsightDetailsComponent extends BaseComponent {
    /**
     * Reference to the content of this component.
     */
    @ViewChild('vulnerabilityInfo', { static: true }) public template: TemplateRef<any>;

    /**
     * Content for the template to be passed to the drawer service.
     */
    public templateContent: SecurityDetailsModel;

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Store the selected item label for page title.
     */
    public selectedAdc: SecurityDetailsModel = {
        adcId: null,
        adcNamespace: null,
        adcLabel: '',
        affectedVersion: '',
        baseSeverity: '',
        cveId: '',
        endAffectedVersion: '',
        productName: '',
        productVendor: null,
        published: null,
        lastModified: null,
        severityScore: null,
        vulnerabilityInfo: '',
        sourceIdentifier: '',
        vectorString: '',
        references: []
    };

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the search term.
     */
    public searchTerm: string;

    constructor(
        private activatedRoute: ActivatedRoute,
        private adcUtilsService: AdcsUtilsService,
        private adcApiService: AdcsApiService,
        private drawerService: DrawerService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private navigationService: NavigationService,
        private rbacService: RbacService,
        private router: Router,
        private securityApiService: SecurityApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;

        this.registerButtons();

        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        if (id) {
            this.setUpPageData(id);
        } else {
            this.logService.error('SecurityDetailsComponent:constructor', 'No ID in url');
        }
    }

    /**
     * Get vendor name for image.
     * @param vendorId
     */
    public getVendorIconName(vendorId: number): string {
        return this.adcUtilsService.getVendorIconName(vendorId);
    }

    /**
     * Get vendor title for span.
     * @param vendorId
     */
    public getVendorTitle(vendorId: number): string {
        return this.adcUtilsService.getVendorTitle(vendorId);
    }

    /**
     * Set class based on severity level.
     * @param severityScore
     */
    public getSeverityScoreClass(severityScore: number): string {
        return this.adcUtilsService.getSeverityScoreClass(severityScore);
    }

    /**
     * Set class based on severity level.
     * @param severityScore
     */
    public getRecommendationText(severityScore: string): string {
        return this.adcUtilsService.getRecommendationText(severityScore.toLowerCase());
    }

    /**
     * Click handler for ADC.
     * @param row
     */
    public openDrawer(row: SecurityDetailsModel): void {
        if ((row && Object.keys(row).length) && row !== this.templateContent) {
            this.templateContent = row;
            this.drawerService.open({ title: row.cveId, content: null, template: this.template });
        } else {
            this.drawerService.close();
            const emptyData = setTimeout(() => {
                this.templateContent = null;
                clearInterval(emptyData);
            }, 500);
        }
    }

    /**
     * CTA to open intercom chat.
     */
    public getHelp(): void {
        const message = 'Hello! My appliance is vulnerable. I have some questions and/or concerns. Can you provide assistance?';
        Intercom('showNewMessage', message);
    }

    /**
     * Update ADC by ID.
     * @param selectedAdc
     */
    public systemUpdateAdc(selectedAdc: any): Observable<any> {
        return new Observable<any>(observe => {
            this.adcApiService.systemUpdate(selectedAdc.adcNamespace, selectedAdc.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.router.navigate(['/cm/adcs/list']).then().catch();
                        observe.next();
                        observe.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('SecurityDetailsComponent: systemUpdateAdc', 'Error submitting data', error);
                        observe.error(error);
                        observe.complete();
                    }
                });
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'security-update-adc': [
                { path: '/api/v1/:namespace/adcs/:id/updates', methods: ['PUT'] },
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Set up page data.
     * @param id
     * @private
     */
    private setUpPageData(id: string) {
        this.securityApiService.getSecurityInsightByAdc(this.selectedNamespaceKey, id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data?.length) {
                        this.selectedAdc = data[0];
                        this.selectedAdc.adcId = parseInt(id, 10);
                        this.populateTableRows(data);
                        this.checkForSearchQuery();
                        this.checkLocalStorageForDrawer();
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SecurityDetailsComponent:setUpPageData', 'Unable to get page data. ' + error);

                }
            });
    }

    /**
     * Check for search query.
     * @private
     */
    private checkForSearchQuery(): void {
        const query = this.utilsService.getObjectValue(this.activatedRoute, 'snapshot.queryParams.sFilter');
        if (query) {
            this.searchTerm = query;
            this.navigationService.removeParamsFromUrl();
        }
    }

    /**
     * Populate table rows.
     * @param data
     * @private
     */
    private populateTableRows(data: any): void {
        this.tableRows = [];
        for (const item of data) {
            this.tableRows.push(
                {
                    affectedVersion: this.getUserFriendlyVersionMessage(item.affectedVersion),
                    baseSeverity: item.baseSeverity,
                    cveId: item.CVEID,
                    endAffectedVersion: item.endAffectedVersion,
                    productName: item.productName?.replaceAll('_', ' '),
                    productVendor: item.productVendor,
                    severityScore: item.baseScore,
                    vulnerabilityInfo: item.description,
                    published: this.utilsService.createDateTimeString(item.published),
                    lastModified: this.utilsService.createDateTimeString(item.lastModified),
                    sourceIdentifier: item.sourceIdentifier,
                    vectorString: item.vectorString,
                    references: this.getReferencesList(item.references)
                }
            );
        }

        this.tableRows = this.utilsService.sortArrayByPropertyAscDesc(this.tableRows, 'severityScore', 'desc');
    }

    /**
     * Get user friendly version message.
     * @param affectVersion
     * @private
     */
    private getUserFriendlyVersionMessage(affectVersion: string): string {
        if (affectVersion.indexOf('<') !== -1) {
            return affectVersion.replace('< ', 'Before v');
        }

        if (affectVersion.indexOf('>') !== -1) {
            return affectVersion.replace('> ', 'After v');
        }

        return affectVersion;
    }

    /**
     * Open drawer with ADC.
     * @private
     */
    private checkLocalStorageForDrawer(): void {
        const drawerState = JSON.parse(localStorage.getItem('drawerState'));
        if (drawerState[location.pathname].visibility) {
            const row = this.tableRows.find(item => item.cveId === drawerState[location.pathname].id);
            this.templateContent = row;
            this.drawerService.open({ title: row.cveId, content: null, template: this.template });
        }
    }

    /**
     * Get resources.
     * @param references
     * @private
     */
    private getReferencesList(references: string): any[] {
        const resourcesList: any[] = [];
        const urls = references ? references.split(',') : [];
        for (const url of urls) {
            const domain = new URL(url);
            resourcesList.push({
                url,
                domain: domain.hostname,
            });
        }

        return resourcesList;
    }
}
